
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../CartProvider';
import Toast from '../Toast';

interface Product {
  id: number;
  name: string;
  category: string;
  price: number;
  originalPrice: number;
  rating: number;
  reviews: number;
  image: string;
  benefits: string[];
  description: string;
}

interface ProductCardProps {
  product: Product;
}

export default function ProductCard({ product }: ProductCardProps) {
  const { dispatch, state } = useCart();
  const [toast, setToast] = useState({ message: '', type: 'success' as const, isVisible: false });
  const [isFavorite, setIsFavorite] = useState(false);

  const isInCart = state.items.some(item => item.id === product.id);

  const handleCartAction = (e: React.MouseEvent) => {
    e.preventDefault();
    
    if (isInCart) {
      dispatch({ type: 'REMOVE_FROM_CART', productId: product.id });
      setToast({
        message: `${product.name} removed from cart!`,
        type: 'success',
        isVisible: true
      });
    } else {
      dispatch({ 
        type: 'ADD_TO_CART', 
        product: {
          id: product.id,
          name: product.name,
          price: product.price,
          image: product.image,
          category: product.category
        }
      });
      setToast({
        message: `${product.name} added to cart!`,
        type: 'success',
        isVisible: true
      });
    }
  };

  const toggleFavorite = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsFavorite(!isFavorite);
  };

  return (
    <>
      <Link to={`/products/${product.id}`} className="block">
        <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 group cursor-pointer">
          <div className="relative overflow-hidden rounded-t-2xl bg-gray-100">
            <div className="w-full h-64 flex items-center justify-center">
              <img 
                src={product.image} 
                alt={product.name}
                className="max-w-full max-h-full object-contain group-hover:scale-110 transition-transform duration-300"
              />
            </div>
            <div className="absolute top-4 left-4">
              <span className="bg-green-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                Save ₹{(product.originalPrice - product.price).toFixed(2)}
              </span>
            </div>
            <div className="absolute top-4 right-4">
              <button 
                onClick={toggleFavorite}
                className="w-8 h-8 flex items-center justify-center bg-white rounded-full shadow-md hover:bg-red-50 transition-colors"
              >
                <i className={`${isFavorite ? 'ri-heart-fill text-red-500' : 'ri-heart-line text-gray-600'}`}></i>
              </button>
            </div>
          </div>

          <div className="p-6">
            <div className="mb-2">
              <span className="text-green-600 text-sm font-medium">{product.category}</span>
            </div>
            
            <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-green-600 transition-colors">
              {product.name}
            </h3>

            <div className="flex items-center mb-3">
              <div className="flex text-yellow-400">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className={`ri-star-${i < Math.floor(product.rating) ? 'fill' : 'line'} text-sm`}></i>
                ))}
              </div>
              <span className="text-gray-600 text-sm ml-2">
                {product.rating} ({product.reviews} reviews)
              </span>
            </div>

            <div className="mb-4">
              {product.benefits.slice(0, 3).map((benefit, index) => (
                <span key={index} className="inline-block bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full mr-2 mb-1">
                  {benefit}
                </span>
              ))}
            </div>

            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <span className="text-2xl font-bold text-green-600">₹{product.price}</span>
                <span className="text-gray-500 line-through text-sm">₹{product.originalPrice}</span>
              </div>
            </div>

            <button 
              onClick={handleCartAction}
              className={`w-full py-3 rounded-full font-semibold transition-colors whitespace-nowrap ${
                isInCart 
                  ? 'bg-red-600 text-white hover:bg-red-700' 
                  : 'bg-green-600 text-white hover:bg-green-700'
              }`}
            >
              {isInCart ? 'Remove from Cart' : 'Add to Cart'}
            </button>
          </div>
        </div>
      </Link>

      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={() => setToast(prev => ({ ...prev, isVisible: false }))}
      />
    </>
  );
}
